/**
 * <shade-customizer>
 *
 * Lets a customer pick a shade design (wave/stripe, single/double, or a
 * raffia-only trim option) and a matching colour combination, with a live
 * SVG preview, then adds the product to the cart itself via the Cart AJAX
 * API — this layout hides the normal PDP add-to-cart form, so there's
 * nothing else on the page to submit through. After a successful add, it
 * dispatches the theme's own CartLinesUpdateEvent so the cart icon and cart
 * drawer update the same way they would for any other add-to-cart.
 */

import { CartLinesUpdateEvent } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const PALETTES_BY_DESIGN = {
  'single-wave': ['1'],
  'single-stripe': ['1'],
  'double-wave': ['1', '2'],
  'double-stripe': ['1', '2'],
  'trim-only': ['trim'],
};

const FIELD_BY_PALETTE = {
  1: 'color1',
  2: 'color2',
  trim: 'trim',
};

const PROPERTY_NAME_BY_FIELD = {
  design: 'Shade Design',
  color1: 'Colour 1',
  color2: 'Colour 2',
  trim: 'Trim Colour',
};

class ShadeCustomizer extends HTMLElement {
  /** @type {Record<string, HTMLInputElement | null>} */
  fields = {};

  /** @type {HTMLSelectElement[]} */
  variantSelects = [];

  /** @type {Array<{ id: number | string, options: string[] }>} */
  variants = [];

  selectedVariantId = '';

  /** @type {HTMLInputElement | null} */
  quantityInput = null;

  connectedCallback() {
    this.designItems = Array.from(this.querySelectorAll('.shade-customizer__design-item'));
    this.palettes = Array.from(this.querySelectorAll('.shade-customizer__palette'));
    this.hint = this.querySelector('[data-hint]');
    this.fields = {
      design: this.querySelector('[data-field="design"]'),
      color1: this.querySelector('[data-field="color1"]'),
      color2: this.querySelector('[data-field="color2"]'),
      trim: this.querySelector('[data-field="trim"]'),
    };

    const addToCartButton = this.querySelector('[data-add-to-cart]');
    this.addToCartButton = addToCartButton instanceof HTMLButtonElement ? addToCartButton : null;
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.addEventListener('click', () => this.addToCart(this.addToCartButton));
    }

    this.designItems.forEach((item) => {
      item.addEventListener('click', () => this.selectDesign(item));
    });

    this.palettes.forEach((palette) => {
      const swatches = palette.querySelectorAll('.shade-customizer__swatch');
      swatches.forEach((swatch) => {
        swatch.addEventListener('click', () => this.selectColor(palette, swatch));
      });
    });

    this.variantSelects = /** @type {HTMLSelectElement[]} */ (
      Array.from(this.querySelectorAll('[data-variant-option]'))
    );
    const variantsScript = this.querySelector('[data-variants-json]');
    this.variants = variantsScript ? JSON.parse(variantsScript.textContent ?? '[]') : [];
    this.selectedVariantId = '';
    this.variantSelects.forEach((select) => {
      select.addEventListener('change', () => this.updateSelectedVariant());
    });
    this.updateSelectedVariant();

    const quantityInput = this.querySelector('[data-field="quantity"]');
    this.quantityInput = quantityInput instanceof HTMLInputElement ? quantityInput : null;
    this.quantityInput?.addEventListener('change', () => this.clampQuantity());
    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.stepQuantity(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.stepQuantity(1));
  }

  /**
   * Recomputes which variant matches the current combination of selected
   * option dropdowns (there's no per-product-page form to resolve this for
   * us, since this component hides the normal PDP buy box).
   */
  updateSelectedVariant() {
    if (!this.variantSelects.length) {
      this.updateAddToCartState();
      return;
    }

    const selectedValues = this.variantSelects.map((select) => select.value);
    const matched = this.variants.find((variant) =>
      selectedValues.every((value, index) => variant.options[index] === value)
    );

    this.selectedVariantId = matched ? String(matched.id) : '';
    this.updateAddToCartState();
  }

  getVariantId() {
    if (this.selectedVariantId) return this.selectedVariantId;
    const fromUrl = new URLSearchParams(window.location.search).get('variant');
    return fromUrl || this.dataset.initialVariantId || '';
  }

  getQuantity() {
    const value = Number(this.quantityInput?.value);
    return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
  }

  /** @param {number} delta */
  stepQuantity(delta) {
    if (!this.quantityInput) return;
    const min = Number(this.quantityInput.min) || 1;
    this.quantityInput.value = String(Math.max(min, this.getQuantity() + delta));
  }

  clampQuantity() {
    if (!this.quantityInput) return;
    this.quantityInput.value = String(this.getQuantity());
  }

  getProperties() {
    /** @type {Record<string, string>} */
    const properties = {};
    Object.entries(this.fields).forEach(([key, input]) => {
      if (input && !input.disabled && input.value) {
        properties[PROPERTY_NAME_BY_FIELD[key]] = input.value;
      }
    });
    return properties;
  }

  /** @param {HTMLButtonElement} button */
  async addToCart(button) {
    const variantId = this.getVariantId();
    if (!variantId) {
      this.showMessage('This product has no purchasable variant.');
      return;
    }

    const quantity = this.getQuantity();
    const properties = this.getProperties();

    /** @type {(value: any) => void} */
    let resolvePromise = () => {};
    /** @type {(reason?: any) => void} */
    let rejectPromise = () => {};
    const promise = new Promise((resolve, reject) => {
      resolvePromise = resolve;
      rejectPromise = reject;
    });

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise,
      })
    );

    button.disabled = true;
    this.showMessage('');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.message || data.description || 'Could not add this to your cart.';
        this.showMessage(message);
        resolvePromise({ cart: null, detail: { didError: true } });
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`);
      const cart = await cartResponse.json();

      resolvePromise({
        cart: { totalQuantity: cart.item_count },
        detail: { itemCount: cart.item_count },
      });

      // Don't rely on cart-items-component picking this update up via the
      // event above and its own data.sections — instead, call the theme's
      // own section renderer directly for every cart-items-component on the
      // page (this re-fetches AND morphs the section's HTML in one call, the
      // exact same function cart-items-component's own fallback path uses).
      document.querySelectorAll('cart-items-component').forEach((el) => {
        if (!(el instanceof HTMLElement) || !el.dataset.sectionId) return;
        sectionRenderer.renderSection(el.dataset.sectionId, {
          cache: false,
          mode: el.dataset.drawer !== undefined ? 'hydration' : 'full',
        });
      });

      const cartIcon = document.querySelector('cart-icon');
      if (cartIcon && typeof cartIcon.renderCartBubble === 'function') {
        cartIcon.renderCartBubble(cart.item_count);
      }

      const cartDrawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (error) {
      console.error('[shade-customizer] add to cart failed', error);
      this.showMessage('Something went wrong adding this to your cart.');
      rejectPromise(error);
    } finally {
      button.disabled = false;
    }
  }

  /** @param {string} text */
  showMessage(text) {
    const message = this.querySelector('[data-add-to-cart-message]');
    if (message) message.textContent = text;
  }

  selectDesign(item) {
    this.designItems.forEach((el) => el.classList.remove('active'));
    item.classList.add('active');

    const design = item.dataset.design;
    const title = item.dataset.title;
    const visiblePalettes = PALETTES_BY_DESIGN[design] || [];

    this.setField('design', title);

    this.palettes.forEach((palette) => {
      const key = palette.dataset.palette;
      const isVisible = visiblePalettes.includes(key);
      palette.hidden = !isVisible;

      if (!isVisible) {
        const field = FIELD_BY_PALETTE[key];
        this.clearField(field);
        palette.querySelectorAll('.shade-customizer__swatch').forEach((swatch) => {
          swatch.classList.remove('active');
        });
      }
    });

    if (this.hint) {
      this.hint.textContent = visiblePalettes.length
        ? 'Choose your colour' + (visiblePalettes.length > 1 ? 's' : '') + ' above.'
        : '';
    }

    this.updateAddToCartState();
  }

  selectColor(palette, swatch) {
    palette.querySelectorAll('.shade-customizer__swatch').forEach((el) => el.classList.remove('active'));
    swatch.classList.add('active');

    const key = palette.dataset.palette;
    const field = FIELD_BY_PALETTE[key];
    const color = swatch.dataset.color;
    const label = swatch.dataset.label;

    this.style.setProperty(`--selected-color${key === 'trim' ? '3' : key}`, color);
    this.setField(field, label);
    this.updateAddToCartState();
  }

  /**
   * Enables "Add to cart" only once a design is chosen and every colour
   * palette that design requires has a colour selected.
   */
  updateAddToCartState() {
    if (!this.addToCartButton) return;

    const design = this.fields.design;
    const hasDesign = Boolean(design && !design.disabled && design.value);

    const visiblePalettes = this.palettes.filter((palette) => !palette.hidden);
    const hasAllColours = visiblePalettes.every((palette) => {
      const field = this.fields[FIELD_BY_PALETTE[palette.dataset.palette]];
      return Boolean(field && !field.disabled && field.value);
    });

    const hasVariant = this.variantSelects.length === 0 || Boolean(this.selectedVariantId);

    this.addToCartButton.disabled = !(hasDesign && hasAllColours && hasVariant);
  }

  setField(name, value) {
    const input = this.fields[name];
    if (!input) return;
    input.value = value;
    input.disabled = false;
  }

  clearField(name) {
    const input = this.fields[name];
    if (!input) return;
    input.value = '';
    input.disabled = true;
  }
}

if (!customElements.get('shade-customizer')) {
  customElements.define('shade-customizer', ShadeCustomizer);
}
